import auth from '@/services/auth';
import gql from '@/services/gql';
import api from '../api';
import { create as saveToDB } from './queries';
import cloudinary from './cloudinary';
import {
  IAsset,
  IFile,
  IDuplicateParams,
  IRemoveParams,
  IUploadOpts,
  IValidation,
} from './types';

interface DefaultResponse {
  affected_rows: number;
  returning: Array<{
    id: string;
    src: string;
  }>;
}

const duplicate = (params: IDuplicateParams) => (
  api
    .post<DefaultResponse>('/asset/duplicate', params)
);

const remove = (params: IRemoveParams) => (
  api
    .post<DefaultResponse>('/asset/remove', params)
    .then(response => response.returning)
);

interface UpdateResponse {
  table: {
    affected_rows: number;
  };
}

// using upsert rather than update as query is easier this way
const updateGql = gql<UpdateResponse>(`
  mutation update_asset(
    $asset: [asset_insert_input!]!
  ) {
    table: insert_asset(
      objects: $asset
      on_conflict: {
        constraint: asset_pkey
        update_columns: [
          index
        ]
      }
    ) {
      affected_rows
    }
  }
`);

const update = (asset: IAsset[]) => updateGql({ asset });

interface SignResponse {
  signedRequest: string;
  url: string;
}

const getSignedUrl = (
  file: IFile,
  category: string,
) => (
  api.get<SignResponse>('/asset/sign', {
    token: auth.getCurrentToken(),
    filename: file.name,
    contentType: file.type,
    category,
  })
);

const uploadFile = (file: IFile, url: string) => (
  // put to url
  api.put(url, file, {
    headers: {
      'Content-Type': file.type,
    },
  })
  .catch((e: any) => {
    console.error(e);
    throw {
      code: e.code || 'ASSET_UPLOAD_ERROR',
    };
  })
);

const validateFile = (
  file: IFile,
  validation = {} as IValidation,
) => (
  Object
    .keys(validation)
    .reduce((
      errorList: string[],
      key: string,
    ): string[] => (
      // if validation fails, add to error list
      validation[key](file)
        ? errorList
        : [...errorList, key]
    ), [])
);

const upload = async (
  file: IFile,
  options: IUploadOpts,
) => {
  const errors = validateFile(file, options.validation);
  if (errors.length) {
    return Promise.reject({
      code: 'VALIDATION_ERROR',
      errors,
    });
  }

  // get file signature for uploading
  const { type: category } = options.record;
  const { signedRequest, url } = await getSignedUrl(file, category);

  // upload file
  await uploadFile(file, signedRequest);

  // save file to db records
  const objects = { ...options.record, src: url };
  const response = await saveToDB({ objects });
  return response.table.returning[0];
};

export * from './types';

export const getCloudinaryUrl = cloudinary;

export default {
  duplicate,
  remove,
  update,
  upload,
};
